import React, { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { noop } from 'ui-components/default-props/noop';

import { csvImportParam, csvJobIdParam } from './constants';
import { useQuery } from '../../../hooks/routing/use-query';
import { InventoryImportDrawer } from '../../../pages/products-page/inventory/modals/import/inventory-import-drawer/inventory-import-drawer';
import Global from '../../../store/global-selectors';
import { ImportType } from '../data/types/import-type';

/**
 * Determines if the import summary should be displayed based on URL parameters and the latest import job.
 *
 * This hook checks if:
 * 1. The current URL has the correct import type parameter
 * 2. The job ID in the URL matches the latest import job of the specified type
 *
 * It's used to decide whether to show the import summary view after an import job completes. -> when user clicks in the notification link
 * When both conditions are true, it means we're viewing the results of the most recent import.
 * @param {import('../data/types/import-type').ImportTypes} importType - The type of import to check
 * @returns {boolean} - True if the summary for the specified import type should be displayed
 */
const useIsImportSummaryOpen = (importType) => {
  const queryParams = useQuery();

  const isImportTypeMatching = queryParams.get(csvImportParam) === importType;

  const latestInventoryImport = useSelector((state) => Global.selectCsvImportType(state, importType));
  const isJobIdMatching = latestInventoryImport && latestInventoryImport.jobId === queryParams.get(csvJobIdParam);

  return isImportTypeMatching && isJobIdMatching;
};
/**
 * Wrapper component that displays CSV import summary drawers based on URL parameters.
 * Currently supports inventory import summaries.
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {JSX.Element} The rendered component
 */
const Wrapper = ({ children }) => {
  const isInventorySummaryOpen = useIsImportSummaryOpen(ImportType.inventory);

  const history = useHistory();
  const onClose = useCallback(() => {
    history.push({ search: '' });
  }, [history]);

  return (
    <>
      {children}
      {/* TODO: implement scan to receive, maybe create custom result component, instead of the specific drawer import
      https://auctane.atlassian.net/browse/SPD-20254 */}
      {isInventorySummaryOpen ? <InventoryImportDrawer onClose={onClose} trackProcess={noop} /> : null}
    </>
  );
};

export const ImportSummaryWrapper = memo(Wrapper);
